import React, { useEffect, useState, useRef, useCallback } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { LoadPanel } from 'devextreme-react/load-panel';
import FormField from '../../../dynamicFormBuilder/FormField';
import data from "../../../assets/apiRoutes.json";
import { LoadPanelPosition, LoadPanelShadingColor } from "../../../const/constants";

// Read-only customer master + default-address detail, mirroring the top panel of
// the AI8 customer query screens. Two groups reproduce the two-column desktop
// layout: master/address on the left, contact info on the right.
const field = (name, label) => ({
    "name": name,
    "tableName": "IDMASTER",
    "label": label,
    "type": "text",
    "required": false,
    "visible": true,
    "isLabelChanged": false,
    "readOnly": true,
});

/**
 * Builds the customer-info layout. The one field that differs between the AI8
 * screens is the second contact number: WM's query screen shows Modem, OMD0010
 * shows Cell Phone 1 (SR-048749). Pass `contactField` to pick.
 */
export const buildCustomerInfoLayout = ({ contactField = "AddressMODEM1", contactLabel = "Modem" } = {}) => ([
    {
        "groupId": 1,
        "fields": [
            field("RECORD", "Record #"),
            field("PROFILECODE", "Profile"),
            field("IDENTITYID", "Customer"),
            field("NAME", "Name"),
            field("AddressADDRESS1", "Address - 1"),
            field("AddressADDRESS2", "Address - 2"),
            field("AddressCITY", "City"),
            field("AddressSTATE", "State"),
            field("AddressZIP", "Zip"),
        ]
    },
    {
        "groupId": 2,
        "fields": [
            field("AddressCOUNTRY", "Country"),
            field("AddressTELEPHONE1", "Telephone"),
            field("AddressTELEPHONE2", "Telephone - 2"),
            field("AddressFAX1", "FAX"),
            field("AddressEMAILADDRESS1", "E-Mail"),
            field(contactField, contactLabel),
            field("AddressWEBSITEADDRESS1", "WebSite"),
        ]
    }
]);

/** Flattens a layout into the list of field names kept in form state. */
export const layoutFieldNames = (layout) =>
    layout.reduce((acc, group) => acc.concat(group.fields.map((f) => f.name)), []);

const emptyFormFields = (fieldNames) => fieldNames.reduce((acc, key) => { acc[key] = ''; return acc; }, {});

/**
 * Fetches one customer through the same endpoint as the Edit Customer screen
 * (id/Customer/GetCustomerDataByRowId) and renders it read-only through the
 * dynamic form builder.
 *
 * Props:
 *   - id               : IDMASTER rowid
 *   - layoutId         : GUID for the dynamic-form layout (unique per host screen)
 *   - layoutName       : dynamic-form layout name
 *   - layout           : optional layout override (see buildCustomerInfoLayout)
 *   - onCustomerLoaded : optional callback receiving the full payload, so the host
 *                        screen's other tabs can reuse it without a second fetch
 */
const CustomerInfoForm = (props) => {
    const { id, layoutId, layoutName, onCustomerLoaded } = props;
    const accessToken = props.oidc.user.access_token;
    const currentUser = props.oidc.user;
    const DYNAMICFORMSETTING = props.oidc?.user?.profile?.DYNAMICFORMSETTING;
    const formRefs = useRef({});
    const layout = useRef(props.layout || buildCustomerInfoLayout()).current;
    const fieldNames = useRef(layoutFieldNames(layout)).current;
    const [formFields, setformFields] = useState(() => emptyFormFields(fieldNames));
    const [loadPanelVisible, setLoadPanelVisible] = useState(false);
    const hideLoadPanel = useCallback(() => {
        setLoadPanelVisible(false);
    }, []);

    useEffect(() => {
        if (!id) {
            return;
        }
        setLoadPanelVisible(true);
        axios.get(`${window._env_.REACT_APP_APIS}/${data.ID.GetCustomerDataByRowId}?rowid=${id}`, {
            headers: {
                access_token: accessToken,
                Authorization: `Bearer ${accessToken}`,
            },
        }).then((res) => {
            const responseData = res.data;
            if (responseData && responseData.ROWID) {
                const mapped = fieldNames.reduce((acc, key) => {
                    acc[key] = responseData[key] ?? '';
                    return acc;
                }, {});
                setformFields(mapped);
                if (onCustomerLoaded) {
                    onCustomerLoaded(responseData);
                }
            }
        }).catch((err) => {
            console.error("Failed to load customer detail:", err);
        }).finally(() => {
            setLoadPanelVisible(false);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleFieldChange = useCallback((name, value) => {
    }, []);

    const handleFormLoad = useCallback(() => {
        // No lookups to preload - every field is read-only text.
    }, []);

    return (
        <div style={{ position: "relative" }}>
            <LoadPanel
                shadingColor={LoadPanelShadingColor}
                position={LoadPanelPosition}
                onHiding={hideLoadPanel}
                visible={loadPanelVisible}
                showIndicator={true}
                shading={true}
                showPane={true}
                hideOnOutsideClick={false}
            />
            <FormField
                currrentUser={currentUser}
                layoutId={layoutId}
                layoutName={layoutName}
                defaultFormSchema={layout}
                formFields={formFields}
                setformFields={setformFields}
                handleFieldValueChange={handleFieldChange}
                DYNAMICFORMSETTING={DYNAMICFORMSETTING}
                formRefs={formRefs}
                onFormLoad={handleFormLoad}
                noSpacingBeforeFirstRow={true}
            />
        </div>
    );
};

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}
export default connect(mapStateToProps)(CustomerInfoForm);
